import {
  Box,
  Button,
  Flex,
  Grid,
  Stack,
  Text,
  chakra,
  shouldForwardProp,
  useTheme,
} from "@chakra-ui/react";
import { isValidMotionProp, motion } from "framer-motion";
import React, { ComponentProps } from "react";
import { CheckIcon, CheckIconEmpty, RadioIcon, RadioIconEmpty } from "./icons";

const ChakraButton = chakra(motion.button, {
  shouldForwardProp: (p) => isValidMotionProp(p) || shouldForwardProp(p),
});

export type OptionProps = OptionPropsSimple | OptionPropsPicture;

type OptionPropsSimple = {
  text: string;
  icon?: React.ReactNode;
  selectorIconType: "radio" | "checkbox";
  size?: "small" | "medium";
} & BaseOptionProps;

export function OptionSimple({
  text,
  icon,
  selectorIconType,
  size = "medium",
  ...rest
}: OptionPropsSimple) {
  const iconSize = size === "small" ? 4 : 6;
  return (
    <BaseOption
      display="flex"
      alignItems="center"
      px={size === "small" ? 2 : 4}
      py={size === "small" ? 1 : 2}
      justifyContent={"center"}
      {...rest}
    >
      <Grid gridTemplateColumns={"auto 1fr auto"} alignItems={"center"} gap={1} width="100%">
        <Box>
          {icon && (
            <Flex
              width={iconSize}
              height={iconSize}
              alignItems={"center"}
              justifyContent={"center"}
            >
              {typeof icon === "string" ? (
                <Text fontSize={size === "small" ? "sm" : "xl"}>{icon}</Text>
              ) : (
                icon
              )}
            </Flex>
          )}
        </Box>

        <Text
          textAlign={"left"}
          fontSize={size === "small" ? "md" : "md"}
          my={size === "small" ? 1 : 2}
          mx={2}
          fontWeight={"500"}
        >
          {text}
        </Text>
        {size === "small" ? null : (
          <OptionInput type={selectorIconType} isSelected={rest.isSelected} />
        )}
      </Grid>
    </BaseOption>
  );
}

type OptionPropsPicture = {
  text: string;
  hideText?: boolean;
  imgHeight: number;
  imgComponent: React.ReactNode;
} & BaseOptionProps;

export function OptionWithPicture({
  imgHeight,
  text,
  imgComponent,
  hideText,
  isSelected,
  ...rest
}: OptionPropsPicture) {
  const theme = useTheme();

  return (
    <ChakraButton
      as={motion.button}
      borderRadius={"xl"}
      border={"4px solid"}
      borderColor={isSelected ? "brand.700" : "#372d30"}
      // @ts-expect-error
      transition={{
        scale: { type: "spring", stiffness: 800, damping: 25 },
      }}
      whileTap={{ scale: 0.98 }}
      color={"text.main"}
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      overflow={"hidden"}
      position={"relative"}
      {...rest}
    >
      <Box maxHeight={imgHeight}>{imgComponent}</Box>
      {!hideText && (
        <>
          <Stack
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height={"50px"}
            bgGradient="linear-gradient(to top, black, transparent)"
            zIndex={1}
            justifyContent={"flex-end"}
          >
            <Text
              width={"100%"}
              textAlign={"center"}
              px={1}
              py={1}
              color={"white"}
              fontSize={"sm"}
              fontWeight={"semibold"}
              lineHeight={1.3}
            >
              {text}
            </Text>
          </Stack>
        </>
      )}
    </ChakraButton>
  );
}

type BaseOptionProps = {
  isSelected: boolean;
} & Omit<ComponentProps<typeof Button>, "size">;

export function BaseOption({ isSelected, children, ...chakraButtonProps }: BaseOptionProps) {
  const theme = useTheme();

  return (
    <ChakraButton
      as={motion.button}
      borderRadius={"2xl"}
      backgroundColor={isSelected ? "#b75175" : "#372d30"}
      // @ts-expect-error
      transition={{
        scale: { type: "spring", stiffness: 800, damping: 25 },
      }}
      whileTap={{ scale: 0.98 }}
      color={"gray.200"}
      {...chakraButtonProps}
    >
      {children}
    </ChakraButton>
  );
}

export function OptionInput({
  type,
  isSelected,
}: {
  type: "radio" | "checkbox";
  isSelected: boolean;
}) {
  const size = "20px";
  const theme = useTheme();
  switch (type) {
    case "checkbox": {
      return isSelected ? (
        <CheckIcon mr={1} width={size} height={size} color={theme.colors.brand["900"]} />
      ) : (
        <CheckIconEmpty mr={1} width={size} height={size} color={theme.colors.brand["600"]} />
      );
    }
    case "radio": {
      return isSelected ? (
        <RadioIcon mr={1} width={size} height={size} color={"white"} />
      ) : (
        <RadioIconEmpty mr={1} width={size} height={size} color={"white"} />
      );
    }
    default: {
      return null;
    }
  }
}
